import type { RequestContext } from "../context/request-context.js";
import type { Project } from "../../domain/project/project-domain.js";
import { isPlhQualifiedProfessional } from "../../domain/identity/plh-eligibility.js";
import { ProfessionalStatus } from "../../domain/identity/professional.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

/**
 * RESOLUÇÃO CENTRAL DOS RESPONSÁVEIS.
 *
 *   CLIENTE ── responsiblePlhProfessionalId (PLH responsável técnico)
 *      └─ PROJETO ── engineerProfessionalId (Engenheiro responsável)
 *
 * O PLH é definido NO CLIENTE e vale para todos os Projetos e documentos
 * daquele Cliente: Documento → Projeto → Cliente → PLH responsável. O
 * Engenheiro responsável continua no Projeto. `Project.plhProfessionalId`
 * é LEGADO e nunca mais é lido como fonte de verdade.
 *
 * Todo documento resolve Engenheiro e PLH por aqui — nunca pelo criador do
 * relatório, último usuário, inspetor ou seleção manual. Não existe regra
 * diferente por tipo de relatório.
 */

export const PROJECT_REQUIRED = "O documento não está vinculado a um Projeto. Vincule o Projeto antes de enviar ao PLH.";
export const PROJECT_NOT_FOUND = "O Projeto vinculado ao documento não foi encontrado em Gestão → Projetos.";
export const CLIENT_REQUIRED = "O documento não está vinculado a um Cliente.";
export const CLIENT_NOT_FOUND = "O Cliente vinculado ao documento não foi encontrado em Administração → Clientes.";
export const CLIENT_WITHOUT_PLH = "O Cliente deste documento ainda não possui um Engenheiro PLH responsável definido. Defina o PLH responsável no cadastro do Cliente.";
/** @deprecated PLH deixou de ser do Projeto — mantido só para imports antigos; o texto é o do Cliente. */
export const PROJECT_WITHOUT_PLH = CLIENT_WITHOUT_PLH;
export const PROJECT_WITHOUT_ENGINEER = "O Projeto não possui Engenheiro responsável definido. Atualize Gestão → Projetos.";

export interface ResolvedResponsible {
  professionalId: string;
  name: string;
  functionTitle: string;
  credential: string;
  userId: string | null;
}

export interface ResponsibilityResolution {
  project: Project | null;
  responsible: ResolvedResponsible | null;
  /** Vazio quando válido; senão, o dado faltante/inválido exatamente. */
  reason: string;
}

/** Resultado do resolvedor central do PLH do CLIENTE (fonte oficial). */
export interface ClientPlhResolution {
  clientId: string | null;
  professionalId: string | null;
  responsible: ResolvedResponsible | null;
  /** Vazio quando válido; senão, o dado faltante/inválido exatamente. */
  reason: string;
  valid: boolean;
}

export type { ResponsibilitySnapshot } from "../../domain/review/technical-document-review.js";
import type { ResponsibilitySnapshot } from "../../domain/review/technical-document-review.js";

interface ProjectLookup {
  getProject(id: string): Promise<Project | null>;
  listProjects?(filter?: { clientId?: string }): Promise<Project[]>;
}

export class ProjectResponsibilityService {
  constructor(
    private readonly projects: ProjectLookup,
    private readonly identityRepo?: any
  ) {}

  private async professionalOf(id: string | null | undefined): Promise<any | null> {
    if (!id || !this.identityRepo?.getProfessional) return null;
    try { return await this.identityRepo.getProfessional(id); } catch { return null; }
  }

  private async clientOf(id: string | null | undefined): Promise<any | null> {
    if (!id || !this.identityRepo?.getClient) return null;
    try { return await this.identityRepo.getClient(id); } catch { return null; }
  }

  private toResolved(p: any): ResolvedResponsible {
    return { professionalId: String(p.id), name: p.name || "", functionTitle: p.functionTitle || "", credential: [p.crea ? `CREA: ${p.crea}` : "", p.cft ? `CFT: ${p.cft}` : ""].filter(Boolean).join(" | "), userId: p.userId ? String(p.userId) : null };
  }

  async getProject(projectId: string | null | undefined): Promise<Project | null> {
    if (!projectId) return null;
    try { return await this.projects.getProject(projectId); } catch { return null; }
  }

  /** ENGENHEIRO RESPONSÁVEL do Projeto (ativo). */
  async resolveProjectEngineer(_context: RequestContext | null, projectId: string | null | undefined): Promise<ResponsibilityResolution> {
    if (!projectId) return { project: null, responsible: null, reason: PROJECT_REQUIRED };
    const project = await this.getProject(projectId);
    if (!project) return { project: null, responsible: null, reason: PROJECT_NOT_FOUND };
    if (!project.engineerProfessionalId) return { project, responsible: null, reason: PROJECT_WITHOUT_ENGINEER };
    const professional = await this.professionalOf(project.engineerProfessionalId);
    if (!professional) return { project, responsible: null, reason: "O Engenheiro responsável do Projeto não foi encontrado no cadastro de Profissionais." };
    if (professional.status !== ProfessionalStatus.Active) return { project, responsible: this.toResolved(professional), reason: "O Engenheiro responsável do Projeto está INATIVO no cadastro." };
    return { project, responsible: this.toResolved(professional), reason: "" };
  }

  /**
   * RESOLVEDOR CENTRAL DO PLH — fonte oficial única: o CLIENTE
   * (`Client.responsiblePlhProfessionalId`). Todos os módulos (RI, ME, LP,
   * PM, US, B-SCAN, AVR, Minuta, Book, TechnicalReview) usam este método,
   * direta ou indiretamente por `resolveProjectPlh`. Nenhum deles repete a
   * regra.
   */
  async resolveClientResponsiblePlh(clientId: string | null | undefined): Promise<ClientPlhResolution> {
    const deny = (reason: string, extra: Partial<ClientPlhResolution> = {}): ClientPlhResolution =>
      ({ clientId: clientId ? String(clientId) : null, professionalId: null, responsible: null, reason, valid: false, ...extra });
    if (!clientId) return deny(CLIENT_REQUIRED);
    const client = await this.clientOf(String(clientId));
    if (!client) return deny(CLIENT_NOT_FOUND);
    const responsibleId = (client as any).responsiblePlhProfessionalId || null;
    if (!responsibleId) return deny(CLIENT_WITHOUT_PLH);
    const professional = await this.professionalOf(String(responsibleId));
    if (!professional) return deny("O PLH responsável do Cliente não foi encontrado no cadastro de Profissionais.", { professionalId: String(responsibleId) });
    // Aqui o responsável JÁ é conhecido — comparar com o clientId era
    // redundância, e era ela que impedia um engenheiro de servir dois Clientes.
    const gate = isPlhQualifiedProfessional(professional, String(responsibleId));
    if (!gate.allowed) {
      return deny(`O PLH responsável do Cliente não está habilitado: ${gate.reason || ""}`, { professionalId: String(responsibleId), responsible: this.toResolved(professional) });
    }
    return { clientId: String(clientId), professionalId: String(professional.id), responsible: this.toResolved(professional), reason: "", valid: true };
  }

  /**
   * PLH RESPONSÁVEL do documento a partir do Projeto: o Projeto só informa o
   * CLIENTE — o PLH vem do cadastro do Cliente (`resolveClientResponsiblePlh`).
   * `Project.plhProfessionalId` é legado e não é lido.
   */
  async resolveProjectPlh(_context: RequestContext | null, projectId: string | null | undefined): Promise<ResponsibilityResolution> {
    if (!projectId) return { project: null, responsible: null, reason: PROJECT_REQUIRED };
    const project = await this.getProject(projectId);
    if (!project) return { project: null, responsible: null, reason: PROJECT_NOT_FOUND };
    const resolution = await this.resolveClientResponsiblePlh(project.clientId);
    return { project, responsible: resolution.responsible, reason: resolution.reason };
  }

  async assertProjectHasValidEngineer(context: RequestContext | null, projectId: string | null | undefined): Promise<ResolvedResponsible> {
    const r = await this.resolveProjectEngineer(context, projectId);
    if (!r.responsible || r.reason) throw new AppError(ErrorCode.Validation, "Project engineer invalid", { safeMessage: r.reason || PROJECT_WITHOUT_ENGINEER });
    return r.responsible;
  }

  async assertProjectHasValidPlh(context: RequestContext | null, projectId: string | null | undefined): Promise<ResolvedResponsible> {
    const r = await this.resolveProjectPlh(context, projectId);
    if (!r.responsible || r.reason) throw new AppError(ErrorCode.Validation, "Project PLH invalid", { safeMessage: r.reason || PROJECT_WITHOUT_PLH });
    return r.responsible;
  }

  /** Engenheiro + PLH como estão HOJE no Projeto (para documento ainda não submetido). */
  async snapshotOf(context: RequestContext | null, projectId: string | null | undefined, clientId: string): Promise<ResponsibilitySnapshot | null> {
    const project = await this.getProject(projectId);
    if (!project) return null;
    const [eng, plh] = await Promise.all([this.resolveProjectEngineer(context, projectId), this.resolveProjectPlh(context, projectId)]);
    return {
      projectId: project.id,
      projectCode: project.code,
      projectName: project.name,
      clientId: project.clientId || clientId,
      engineerProfessionalId: eng.responsible?.professionalId ?? null,
      engineerName: eng.responsible?.name ?? "",
      engineerFunctionTitle: eng.responsible?.functionTitle ?? "",
      engineerCredential: eng.responsible?.credential ?? "",
      plhProfessionalId: plh.responsible?.professionalId ?? null,
      plhName: plh.responsible?.name ?? "",
      plhFunctionTitle: plh.responsible?.functionTitle ?? "",
      plhCredential: plh.responsible?.credential ?? "",
      frozenAt: new Date().toISOString()
    };
  }

  /**
   * MIGRAÇÃO (somente leitura): relatório dos PLHs legados gravados em
   * `Project.plhProfessionalId`, agrupados por Cliente. Nada é copiado nem
   * sobrescrito — quando os Projetos de um mesmo Cliente apontam para PLHs
   * diferentes, ou quando o Cliente já tem um responsável divergente, o
   * sistema NÃO escolhe sozinho: a definição é manual no cadastro do Cliente.
   */
  /**
   * CONFLITOS DO `Professional.plhClientId` LEGADO.
   *
   * Desde 2026-09-24 a habilitação vive em `Client.responsiblePlhProfessionalId`
   * e `plhClientId` deixou de ser lido. Os valores antigos não são apagados
   * nem promovidos automaticamente — o sistema nunca escolhe um PLH sozinho.
   * Este relatório mostra onde os dois registros discordam, para decisão
   * humana, no mesmo espírito de `legacyProjectPlhConflicts`.
   *
   * Dois casos:
   *   ORFAO   — o Professional diz ser PLH de um Cliente que não o nomeou.
   *   DIVERGE — o Cliente nomeou outra pessoa.
   */
  async legacyProfessionalPlhConflicts(): Promise<Array<{
    professionalId: string; professionalName: string; legacyClientId: string;
    clientName: string; clientResponsiblePlhProfessionalId: string | null;
    kind: "ORFAO" | "DIVERGE";
  }>> {
    const professionals: any[] = this.identityRepo?.listProfessionals ? await this.identityRepo.listProfessionals() : [];
    const rows: Array<{ professionalId: string; professionalName: string; legacyClientId: string; clientName: string; clientResponsiblePlhProfessionalId: string | null; kind: "ORFAO" | "DIVERGE" }> = [];

    for (const professional of professionals) {
      if (professional.deletedAt || !professional.plhClientId) continue;
      const legacyClientId = String(professional.plhClientId);
      const client = await this.clientOf(legacyClientId);
      const responsible = client?.responsiblePlhProfessionalId ? String(client.responsiblePlhProfessionalId) : null;
      if (responsible === String(professional.id)) continue; // os dois concordam
      rows.push({
        professionalId: String(professional.id), professionalName: professional.name || String(professional.id),
        legacyClientId, clientName: client?.name || legacyClientId,
        clientResponsiblePlhProfessionalId: responsible,
        kind: responsible ? "DIVERGE" : "ORFAO"
      });
    }
    return rows;
  }

  async legacyProjectPlhConflicts(): Promise<Array<{ clientId: string; currentResponsiblePlhProfessionalId: string | null; legacyByProject: Array<{ projectId: string; projectCode: string; plhProfessionalId: string }>; distinctLegacyPlhIds: string[]; requiresManualDecision: boolean }>> {
    if (!this.projects.listProjects) return [];
    const all = await this.projects.listProjects();
    const byClient = new Map<string, Array<{ projectId: string; projectCode: string; plhProfessionalId: string }>>();
    for (const project of all) {
      const legacy = project.plhProfessionalId ? String(project.plhProfessionalId) : "";
      if (!legacy) continue;
      const clientId = String(project.clientId || "");
      const rows = byClient.get(clientId) ?? [];
      rows.push({ projectId: project.id, projectCode: project.code, plhProfessionalId: legacy });
      byClient.set(clientId, rows);
    }
    const report = [];
    for (const [clientId, legacyByProject] of byClient) {
      const client = await this.clientOf(clientId);
      const current = (client as any)?.responsiblePlhProfessionalId ? String((client as any).responsiblePlhProfessionalId) : null;
      const distinctLegacyPlhIds = [...new Set(legacyByProject.map(r => r.plhProfessionalId))];
      report.push({
        clientId,
        currentResponsiblePlhProfessionalId: current,
        legacyByProject,
        distinctLegacyPlhIds,
        requiresManualDecision: distinctLegacyPlhIds.length > 1 || (!!current && !distinctLegacyPlhIds.includes(current)) || !current
      });
    }
    return report;
  }

  /**
   * Projetos em que o Profissional atua — leitura para o cadastro de
   * Profissionais. `asEngineer` vem do Projeto; `asPlh` vem do Cliente do
   * Projeto (PLH responsável do Cliente), nunca do campo legado do Projeto.
   */
  async projectsOfProfessional(professionalId: string): Promise<Array<{ project: Project; asEngineer: boolean; asPlh: boolean }>> {
    if (!this.projects.listProjects) return [];
    const all = await this.projects.listProjects();
    const plhByClient = new Map<string, boolean>();
    const isPlhOfClient = async (clientId: string): Promise<boolean> => {
      const cached = plhByClient.get(clientId);
      if (cached !== undefined) return cached;
      const client = await this.clientOf(clientId);
      const value = String((client as any)?.responsiblePlhProfessionalId || "") === String(professionalId);
      plhByClient.set(clientId, value);
      return value;
    };
    const rows: Array<{ project: Project; asEngineer: boolean; asPlh: boolean }> = [];
    for (const project of all) {
      const asEngineer = String(project.engineerProfessionalId || "") === String(professionalId);
      const asPlh = await isPlhOfClient(String(project.clientId || ""));
      if (asEngineer || asPlh) rows.push({ project, asEngineer, asPlh });
    }
    return rows;
  }
}
